import { AlertCircle, Maximize2 } from "lucide-react";
import { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AssistantTranscript as VoiceTranscript } from "@/features/voice-agent/components/AssistantTranscript";
import { VoiceAurora } from "@/features/voice-agent/components/VoiceAurora";
import { VoiceComposer } from "@/features/voice-agent/components/VoiceComposer";
import { AssistantDocumentProposal } from "@/features/voice-agent/components/AssistantDocumentProposal";
import { useVoiceAssistant } from "@/features/voice-agent/VoiceAssistantContext";
import { canChangeDirectTextProvider } from "@/features/voice-agent/hooks/use-direct-text";
import { ASSISTANT_PATH, isAssistantPathname } from "@/features/voice-agent/assistant-route";
import { useAssistantThreads } from "@/features/voice-agent/assistant-threads";

const STATUS_COPY = {
  connecting: "Connecting…",
  error: "Tap to retry",
  idle: "Tap to talk",
  listening: "Listening…",
  speaking: "Speaking…",
} as const;

const LAUNCHER_TITLE_ID = "voice-assistant-launcher-title";

function canChangeTextProvider(mode: "text" | "live" | undefined, message?: string): boolean {
  return mode === "text" && canChangeDirectTextProvider(message);
}

/** The thread the launcher continues; the next message goes to it. */
function CurrentThreadTitle({
  actorId,
  threadId,
}: {
  actorId: string | null;
  threadId?: string | null;
}) {
  // An archived thread keeps its title too: archiving does not close it here.
  const active = useAssistantThreads(threadId ? actorId : null);
  const archived = useAssistantThreads(threadId ? actorId : null, true);
  const title = [...(active.data ?? []), ...(archived.data ?? [])].find(
    (thread) => thread.threadId === threadId,
  )?.title;
  if (!threadId) return null;
  return (
    <p className="max-w-56 truncate text-muted-foreground text-xs">{title || "Current thread"}</p>
  );
}

export function VoiceAssistantLauncher() {
  const { pathname } = useLocation();
  const session = useVoiceAssistant();
  const documentProposals = session.documentProposals;
  const recentTurns = useMemo(() => session.turns.slice(-3), [session.turns]);
  if (isAssistantPathname(pathname)) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          aria-label={`Open voice assistant. ${STATUS_COPY[session.status]}`}
          className="fixed right-(--shell-gutter-right) bottom-(--launcher-voice-bottom) z-40 grid size-(--launcher-size) place-items-center rounded-full border border-border bg-background shadow-xl transition hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:right-[calc(1.5rem+var(--shell-inset-right))]"
          type="button"
        >
          <VoiceAurora
            className="size-14"
            isTyping={Boolean(session.textPending)}
            status={session.status}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        aria-labelledby={LAUNCHER_TITLE_ID}
        className="mb-2 max-h-[var(--radix-popover-content-available-height)] w-[calc(100vw-1rem)] max-w-md overflow-y-auto overscroll-contain rounded-3xl p-0 shadow-xl"
        collisionPadding={8}
        sideOffset={8}
      >
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <p className="font-semibold" id={LAUNCHER_TITLE_ID}>
              Voice Assistant
            </p>
            <CurrentThreadTitle actorId={session.actorId ?? null} threadId={session.threadId} />
            <p aria-live="polite" className="text-muted-foreground text-xs">
              {STATUS_COPY[session.status]}
            </p>
          </div>
          <Button asChild size="mobileTouchIcon" variant="ghost">
            <Link aria-label="Open full assistant" to={ASSISTANT_PATH}>
              <Maximize2 aria-hidden="true" className="size-4" />
            </Link>
          </Button>
        </header>
        <div className="grid min-h-56 grid-cols-[6rem_1fr] items-center gap-3 p-4">
          <button
            aria-label={
              session.status === "connecting"
                ? "Voice assistant connecting"
                : session.status === "idle" || session.status === "error"
                  ? "Start voice conversation"
                  : "End voice conversation"
            }
            disabled={session.status === "connecting"}
            className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            onClick={session.toggle}
            type="button"
          >
            <VoiceAurora
              className="size-24"
              isTyping={Boolean(session.textPending)}
              status={session.status}
            />
          </button>
          <div className="min-w-0">
            {recentTurns.length ? (
              <VoiceTranscript className="max-h-52" turns={recentTurns} />
            ) : (
              <p className="text-muted-foreground text-sm">
                Type for silent replies. Start live voice to talk.
              </p>
            )}
          </div>
        </div>
        {session.errorMessage && (
          <Alert className="mx-3 mb-3 w-auto" variant="destructive">
            <AlertCircle aria-hidden="true" className="size-4" />
            <AlertTitle>Voice agent problem</AlertTitle>
            <AlertDescription>
              {session.errorMessage}
              {canChangeTextProvider(session.mode, session.errorMessage) && (
                <>
                  {" "}
                  <Link className="underline" to="/settings">
                    Change assistant provider
                  </Link>
                </>
              )}
            </AlertDescription>
          </Alert>
        )}
        {documentProposals?.proposals.map((proposal) => (
          <div className="mx-3 mb-3" key={`${proposal.proposalId}-${proposal.revision}`}>
            <AssistantDocumentProposal
              onApprove={(item, challenge) => documentProposals.approve({ item, challenge })}
              onCancel={documentProposals.cancel}
              onChallenge={documentProposals.challenge}
              onDraftCustomerMessage={documentProposals.customerMessage}
              onRevise={(item) =>
                session.setDraft(
                  `Please revise ${item.documentKind} proposal revision ${item.revision}: `,
                )
              }
              onVoiceApprove={session.approveByVoice}
              onStopVoiceApproval={session.stopVoiceApproval}
              onCancelVoiceApproval={session.cancelVoiceApproval}
              voiceApprovalAvailable={session.voiceApprovalAvailable}
              proposal={proposal}
            />
          </div>
        ))}
        <VoiceComposer className="m-3 mt-0 shadow-none" />
      </PopoverContent>
    </Popover>
  );
}
